import { cn } from '@/shared/lib/cn'

export interface SegmentedOption<T extends string> {
  value: T
  label: string
  /** Texto para lectores de pantalla cuando el label es solo un numero. */
  title?: string
}

export interface SegmentedProps<T extends string> {
  value: T
  options: readonly SegmentedOption<T>[]
  onChange: (value: T) => void
  label: string
  className?: string
}

/**
 * Conmutador de una sola linea (vista, horizonte, agrupacion).
 *
 * `role="radiogroup"` y no una fila de botones sueltos: con teclado se recorre
 * como un grupo, que es como se usa esta app.
 */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
  className,
}: SegmentedProps<T>) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn(
        'hairline inline-flex h-7 items-center rounded-[var(--radius-control)] p-0.5',
        className,
      )}
    >
      {options.map((option) => {
        const active = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            title={option.title ?? option.label}
            onClick={() => onChange(option.value)}
            className={cn(
              'h-6 rounded-[var(--radius-control)] px-2 text-2xs whitespace-nowrap',
              'transition-colors duration-[var(--motion-fast)]',
              active ? 'bg-surface-2 text-text-1' : 'text-text-2 hover:text-text-1',
            )}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
